import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, Building2, Users, GraduationCap, ClipboardCheck,
  CalendarCheck, ShieldCheck, Scale, ArrowRight, CheckCircle2,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import HeroVisual from "@/components/HeroVisual";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import ProcessTimeline from "@/components/ProcessTimeline";
import InsightsGrid from "@/components/InsightsGrid";
import { rotatingHeadlines, accreditations, whyUs, trackRecord, outcomes, edgePoints } from "@/data/site";
import { services } from "@/data/services";

const serviceIcons = { Delivery: Rocket, PMO: Building2, Resourcing: Users, Training: GraduationCap, Review: ClipboardCheck };
const outcomeIcons = { CalendarCheck, Users, ShieldCheck, Scale };

function RotatingHeadline() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % rotatingHeadlines.length), 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative min-h-[9rem] sm:min-h-[11rem] lg:min-h-[12rem]">
      <AnimatePresence mode="wait">
        <motion.h1
          key={idx}
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -18 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="text-balance font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
          data-testid="hero-rotating-headline"
        >
          {rotatingHeadlines[idx]}
        </motion.h1>
      </AnimatePresence>
    </div>
  );
}

const particles = [
  { top: "12%", left: "6%", size: 6, delay: "0s" },
  { top: "30%", left: "48%", size: 4, delay: "1.4s" },
  { top: "70%", left: "12%", size: 5, delay: "2.2s" },
  { top: "18%", left: "82%", size: 4, delay: "0.8s" },
  { top: "62%", left: "90%", size: 6, delay: "1.9s" },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Incremental Consulting | Programme Delivery, PMO & Resourcing South Africa"
        description="Senior programme and PMO leadership, vetted delivery resourcing and independent delivery assurance for South African financial institutions and regulated businesses. 100% Black-owned, B-BBEE Level 1."
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B1F3A]" data-testid="hero-section">
        <img
          src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1920&q=80"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-[#0B1F3A]/55" aria-hidden="true" />
        {particles.map((p, i) => (
          <span
            key={i}
            className="ic-particle absolute rounded-full bg-[#C9A227]/40"
            style={{ top: p.top, left: p.left, width: p.size, height: p.size, animationDelay: p.delay }}
            aria-hidden="true"
          />
        ))}
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
          <div>
            <Reveal>
              <p className="inline-flex items-center gap-2 rounded-md border border-[#C9A227]/50 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#C9A227]" data-testid="hero-eyebrow">
                Programme Delivery, PMO & Resourcing — South Africa
              </p>
            </Reveal>
            <div className="mt-6">
              <RotatingHeadline />
            </div>
            <Reveal delay={0.15}>
              <p className="mt-2 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg" data-testid="hero-subtext">
                Senior programme and PMO leadership, and vetted delivery professionals, for financial institutions and regulated businesses under deadline pressure — from a firm that has run the programmes, not just advised on them.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-lets-talk"
                className="group inline-flex items-center gap-2 rounded-md bg-[#C9A227] px-7 py-3.5 text-sm font-bold text-[#0B1F3A] transition-colors duration-200 hover:bg-[#DDB845]"
              >
                Book a delivery review
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-explore-services"
                className="inline-flex items-center gap-2 rounded-md border border-white/35 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-[#C9A227] hover:text-[#C9A227]"
              >
                See what we do
              </Link>
            </Reveal>
            <Reveal delay={0.35} className="mt-10 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-accreditations">
              {accreditations.map((a) => (
                <span key={a} className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-white/75">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
                  {a}
                </span>
              ))}
            </Reveal>
          </div>
          <HeroVisual />
        </div>
      </section>

      <Marquee />

      {/* DEADLINE */}
      <section className="bg-[#F5F8FC]" data-testid="deadline-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="When the deadline can't move"
            title="Meeting your deadlines is your reputation."
            testId="deadline"
          />
          <Reveal delay={0.15}>
            <p className="font-display text-lg font-bold text-[#A87B1F]">We put senior delivery people on the programmes that can't slip.</p>
            <p className="mt-4 text-[15px] leading-relaxed text-[#172033]/85">
              ISO 20022 in November 2026. FATF evidence through 2027. COFI on its way. We lead the delivery, stand up the PMO, and resource the teams that get regulated change over the line — and when you need an independent read, we run a fixed-scope delivery assurance review.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bg-white" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="What We Do"
            title="Senior delivery capability, five ways."
            sub="Programmes led, offices stood up, teams resourced and certified — and independently reviewed when you need a second opinion. Each is something our clients already procure."
            testId="services"
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => {
              const Icon = serviceIcons[s.category];
              const isReview = s.category === "Review";
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.1}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className={`group flex h-full flex-col rounded-xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
                      isReview
                        ? "border-[#C9A227]/40 bg-[#F5F8FC] hover:border-[#C9A227] hover:shadow-[0_20px_44px_-20px_rgba(201,162,39,0.45)]"
                        : "border-[#0B1F3A]/10 bg-white hover:border-[#C9A227]/60 hover:shadow-[0_20px_44px_-20px_rgba(11,31,58,0.3)]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A87B1F]">{s.category}</span>
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#C9A227]/15 text-[#A87B1F] transition-colors duration-300 group-hover:bg-[#C9A227] group-hover:text-[#0B1F3A]">
                        <Icon size={19} />
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-lg font-bold text-[#0B1F3A]">{s.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-[#667085]">{s.short}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#A87B1F]">
                      Explore service
                      <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
            <Reveal delay={0.2}>
              <div className="flex h-full flex-col justify-center rounded-xl bg-[#0B1F3A] p-7" data-testid="services-cta-card">
                <p className="font-display text-lg font-bold leading-snug text-white">Not sure which one fits the programme?</p>
                <Link to="/services" data-testid="services-explore-all" className="group mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#C9A227] hover:text-[#DDB845]">
                  Explore our services
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="bg-[#F5F8FC]" data-testid="how-we-work-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="How We Work"
            title="A structured path from mandate to confidence."
            testId="how-we-work"
          />
          <div className="mt-14">
            <ProcessTimeline />
          </div>
        </div>
      </section>

      {/* WHY INCREMENTAL */}
      <section className="relative overflow-hidden bg-[#0B1F3A]" data-testid="why-us-section">
        <img
          src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1600&q=80"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.1]"
        />
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Why Incremental"
            title="Senior people who have run the work — with financial governance built in."
            light
            testId="why-us"
          />
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {whyUs.map((w, i) => (
              <Reveal key={w.label} delay={i * 0.1}>
                <div data-testid={`why-card-${w.label.toLowerCase()}`}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#C9A227]">{w.label}</p>
                  <h3 className="mt-3 font-display text-lg font-bold leading-snug text-white">{w.title}</h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-white/60">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRACK RECORD */}
      <section className="bg-[#F5F8FC]" data-testid="track-record-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Track Record"
            title="Delivery led across South Africa's major institutions."
            sub="Programmes our founder has led at Vodacom, Nedbank and Standard Bank — building teams, scaling agile and landing regulatory change."
            testId="track-record"
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {trackRecord.map((t, i) => (
              <Reveal key={t.org} delay={i * 0.12}>
                <article className="flex h-full flex-col rounded-xl bg-[#0B1F3A] p-7" data-testid={`track-card-${t.org.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-xl font-extrabold text-[#C9A227]">{t.org}</h3>
                    <span className="rounded-full border border-white/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/70">{t.type}</span>
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-white/75">{t.text}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {t.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-white/10 px-3 py-1 text-[10.5px] font-bold uppercase tracking-wider text-white/70">{tag}</span>
                    ))}
                  </div>
                  <p className="mt-5 flex gap-2 border-t border-white/10 pt-4 text-[13px] leading-relaxed text-white/85">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#C9A227]" />
                    <span><span className="font-bold text-white">Outcome:</span> {t.outcome}</span>
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-[13px] text-[#667085]" data-testid="track-record-disclaimer">
              Programmes led by our founder. Current client engagements are held under confidentiality; references available on request.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="bg-white" data-testid="results-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading eyebrow="Outcomes" title="What confident delivery looks like." testId="results" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {outcomes.map((r, i) => {
              const Icon = outcomeIcons[r.icon];
              return (
                <Reveal key={r.title} delay={i * 0.1}>
                  <div className="h-full rounded-xl border border-[#0B1F3A]/10 bg-[#F5F8FC] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#C9A227]/50" data-testid={`result-card-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#C9A227]/15 text-[#A87B1F]">
                      <Icon size={22} />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold text-[#0B1F3A]">{r.title}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#667085]">{r.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR EDGE */}
      <section className="bg-[#F5F8FC]" data-testid="edge-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Our Edge"
            title="Delivery discipline meets financial governance."
            sub="What sets us apart from pure delivery houses is a Chartered Accountant on the board, applied directly to how programmes are delivered and assured — where delivery meets money and audit scrutiny."
            testId="edge"
          />
          <Reveal delay={0.15}>
            <ul className="space-y-6">
              {edgePoints.map((p) => (
                <li key={p.title} className="flex gap-3">
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#A87B1F]" />
                  <div>
                    <p className="font-display text-[15px] font-bold text-[#0B1F3A]">{p.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-[#667085]">{p.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="bg-white" data-testid="home-about-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
          <Reveal className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#A87B1F]">The People Behind It</p>
              <h2 className="mt-3 text-balance font-display text-2xl font-bold tracking-tight text-[#0B1F3A] sm:text-3xl" data-testid="home-about-title">
                Led by practitioners, not career consultants.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[#667085]">
                A founder with a decade of financial-services delivery and a Chartered Accountant director, pairing delivery discipline with financial governance under one roof.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="home-about-cta"
              className="group inline-flex shrink-0 items-center gap-2 rounded-md bg-[#C9A227] px-7 py-3.5 text-sm font-bold text-[#0B1F3A] transition-colors duration-200 hover:bg-[#DDB845]"
            >
              Meet the team
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="bg-[#F5F8FC]" data-testid="insights-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Insights"
            title="Thinking on better delivery."
            sub="Perspectives on delivery assurance, regulatory programmes and flow. New articles are on the way."
            testId="insights"
          />
          <InsightsGrid testIdPrefix="home-insight" limit={3} />
          <Reveal delay={0.2} className="mt-8">
            <Link to="/insights" data-testid="insights-view-all-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#A87B1F] hover:text-[#0B1F3A]">
              View all insights
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
